import { get, patch, post } from "./client";
import {
  companyReviewPath,
  masterCompaniesService,
  type CompanyReviewPreview,
  type CompanySortDirection,
} from "./masterCompanies.service";

export type { CompanyReviewPreview } from "./masterCompanies.service";

export type ReviewStatus = "pending" | "approved" | "rejected";
export type PendingCompanySortField =
  | "name"
  | "createdAt"
  | "status"
  | "userId"
  | "resolvedCompany";
export type PendingCompany = {
  id: string;
  userId: string;
  name: string;
  status: ReviewStatus;
  revision: number;
  companyId: string | null;
  companyRevision: number | null;
  resolvedCompanyName: string | null;
  resolutionKind: "created_new" | "linked_existing" | null;
  createdAt: string;
  updatedAt: string;
};
export type CompanyReviewEvent = {
  id: string;
  submissionId: string | null;
  companyId: string | null;
  action: string;
  previousCompanyId?: string | null;
  source: "manual" | "import" | "approval" | null;
  actorId: string;
  actorEmail: string | null;
  createdAt: string;
  previousName: string | null;
  newName: string | null;
  reason: string | null;
  revision: number;
  profileLinked: boolean | null;
  updatedProfileCount: number | null;
  createdCompany: boolean | null;
};
export type ReviewPage<T> = { data: T[]; meta: { total: number; limit: number; offset: number } };
const base = "/v1/website-pending-companies";
export const pendingCompaniesService = {
  list: (
    search: string,
    limit: number,
    offset: number,
    sort: PendingCompanySortField,
    direction: CompanySortDirection,
    status: ReviewStatus | "all" = "all",
  ) =>
    get<ReviewPage<PendingCompany>>(
      `${base}?${new URLSearchParams({ search, sort, direction, status, limit: String(limit), offset: String(offset) })}`,
    ),
  detail: (id: string) => get<{ data: PendingCompany }>(`${base}/${id}`),
  edit: (id: string, name: string, reason: string, revision: number) =>
    patch<{ data: PendingCompany }>(`${base}/${id}`, { name, reason, revision }),
  approve: (
    id: string,
    revision: number,
    reason: string,
    companyId?: string,
    companyRevision?: number,
  ) =>
    post<{ data: PendingCompany }>(`${base}/${id}/approve`, {
      revision,
      reason,
      ...(companyId ? { companyId, companyRevision } : {}),
    }),
  reject: (id: string, revision: number, reason: string) =>
    post<{ data: PendingCompany }>(`${base}/${id}/reject`, { revision, reason }),
  audit: (id: string, limit: number, offset: number) =>
    get<ReviewPage<CompanyReviewEvent>>(
      `${base}/${id}/audit?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`,
    ),
  preview: masterCompaniesService.review,
  correct: (
    id: string,
    name: string,
    reason: string,
    revision: number,
    entry?: { submissionId: string; submissionRevision: number },
  ) =>
    patch<{ data: CompanyReviewPreview }>(companyReviewPath(id), {
      name,
      reason,
      revision,
      ...entry,
    }),
};
